// ExpansionLanding.js — iter201
//
// Premium first-impression landing page for the Restaurant Expansion
// Intelligence platform. Sits BEFORE the existing login flow. Theme:
// RestroPulse green + white + Montserrat. No new workflows, no new
// dashboards — clicking Login takes the user to the existing /login
// route, and post-login they land directly in the existing EI workspace.

import React from 'react';
import { useNavigate } from 'react-router-dom';

export default function ExpansionLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-slate-900"
         data-testid="ei-landing-root">
      {/* ── Top bar ───────────────────────────────────────────────── */}
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/restropulse-logo.png" alt="RestroPulse"
                 className="w-10 h-10" />
            <div>
              <div className="text-lg font-bold tracking-tight text-emerald-900"
                   data-testid="ei-landing-brand">RestroPulse</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">
                Restaurant Expansion Intelligence
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/login')}
                    className="text-sm font-semibold text-emerald-800 hover:text-emerald-950
                               px-4 py-2 rounded-md transition-colors"
                    data-testid="ei-landing-login-link">
              Login
            </button>
          </div>
        </div>
      </header>

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <main className="max-w-6xl mx-auto px-6 sm:px-10 pt-20 pb-24">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase
                            tracking-[0.2em] text-emerald-700 bg-emerald-50
                            border border-emerald-100 rounded-full px-3 py-1
                            mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
              Consultant-grade expansion analysis
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]
                            tracking-tight text-slate-900 mb-6"
                data-testid="ei-landing-headline">
              Restaurant Expansion Intelligence
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-10">
              Evaluate locations, concepts, feasibility and investment opportunities
              before committing capital. Every report is reproducible, traceable,
              and ready for the boardroom.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={() => navigate('/login')}
                      className="bg-emerald-700 hover:bg-emerald-800 text-white
                                 font-semibold text-sm px-7 py-3 rounded-md
                                 transition-colors shadow-sm"
                      data-testid="ei-landing-primary-login">
                Login
              </button>
              <button onClick={() => {
                        window.location.href =
                          'mailto:hello@restropulse.com?subject=Request%20Access%20—%20Expansion%20Intelligence';
                      }}
                      className="border border-slate-300 hover:border-slate-500
                                 text-slate-800 font-semibold text-sm px-7 py-3
                                 rounded-md transition-colors"
                      data-testid="ei-landing-request-access">
                Request Access
              </button>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              <Stat label="Sections per report" value="18" />
              <Stat label="Visual charts"       value="12" />
              <Stat label="Sheets per workbook" value="11" />
            </div>
          </div>

          {/* ── Right column: 4 value pillars ───────────────────── */}
          <div className="space-y-3">
            {[
              { kicker: '01 · Market Intelligence',
                title: 'Evidence-weighted catchment DNA',
                body:  'Anchor density, cuisine HHI, format saturation, competitor depth — all sourced from live Google Places signals.' },
              { kicker: '02 · Concept ↔ Market Fit',
                title: '8-dimension fit scoring + USP intelligence',
                body:  'See exactly which fit dimensions trail their bands and which strengths compound your USP.' },
              { kicker: '03 · Project Feasibility',
                title: 'CAPEX · Property · Execution · Regulatory',
                body:  'Editable space allocation, traceable CAPEX line items, regulatory gate analysis.' },
              { kicker: '04 · Financial Viability',
                title: '36-month P&L · break-even · payback · sensitivity',
                body:  'Override APC and occupancy with reasoning; sensitivity matrix surfaces fragility before commitments.' },
            ].map((p) => (
              <Pillar key={p.title} {...p} />
            ))}
          </div>
        </div>

        {/* ── Trust strip ────────────────────────────────────────── */}
        <div className="mt-24 border-t border-slate-100 pt-10">
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500
                          mb-6">Built for</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {['Restaurateurs', 'Investors', 'Landlords', 'Franchise Partners']
              .map((aud) => (
                <div key={aud} className="text-sm font-semibold text-slate-700">
                  {aud}
                </div>
              ))}
          </div>
        </div>
      </main>

      {/* ── Footer ────────────────────────────────────────────────── */}
      <footer className="border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-6 flex flex-col
                        sm:flex-row gap-3 sm:items-center justify-between
                        text-xs text-slate-500">
          <div>© RestroPulse · Confidential — for investor and consultant use.</div>
          <div className="flex gap-4">
            <a href="/legal/privacy" className="hover:text-slate-800">Privacy</a>
            <a href="/legal/terms"   className="hover:text-slate-800">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-3xl font-bold text-emerald-900 leading-none">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1.5">{label}</div>
    </div>
  );
}

function Pillar({ kicker, title, body }) {
  return (
    <div className="border border-slate-100 rounded-md bg-white p-5
                    hover:border-emerald-200 hover:shadow-sm transition-all"
         data-testid={`ei-landing-pillar-${kicker.split(' ')[0]}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 mb-1.5">
        {kicker}
      </div>
      <div className="text-base font-bold text-slate-900 mb-1">{title}</div>
      <div className="text-sm text-slate-600 leading-relaxed">{body}</div>
    </div>
  );
}
